/**
 * The first 12 insight rules (blueprint §19). Each rule declares its
 * operationalPriority tier (P1 FF safety → P2 others → P3 property → P4 normality)
 * which governs suppression and ranking. Rules may also declare a minStage so the
 * engine stage-gates them and avoids surfacing overhaul prompts during size-up.
 */

import {
  isConfirmedFire,
  SHOWING_LABELS,
  FIRE_STATUS_LABELS,
  FIRE_STATUS_CONTROLLED,
  labelForTag,
  type Incident,
} from '../incident.js';
import { isAtOrAfter, stageIndex } from '../stages.js';
import { summariseCapability, type CapabilitySummary } from '../resources.js';
import { assessWater, type WaterAssessment } from '../water.js';
import { baOperatorRequirement, evStorageExclusionRequirement } from '../doctrine.js';
import { rosterBAOperators, rosterCrewCount } from '../member.js';
import type { DutyConfig } from '../config.js';
import type { RuleDef, InsightContext, ActionSpec } from './types.js';
import type { IncidentType } from '../incident-type.js';

// --- small predicates --------------------------------------------------------

const confirmed = (i: Incident): boolean => isConfirmedFire(i.status);

/** Life risk is unresolved when no life read, or it reads as unknown/unconfirmed. */
export function lifeUnresolved(i: Incident): boolean {
  const life = i.itasc.threats.life?.trim();
  return !life || /unknown|unconfirmed|not\s+confirmed|tbc|nil known/i.test(life);
}

const threat = (i: Incident, t: string): boolean => i.itasc.threats.selected.includes(t as never);
const requested = (i: Incident, s: string): boolean => i.itasc.support.requested.includes(s as never);
const action = (i: Incident, a: string): boolean => i.itasc.actions.selected.includes(a as never);

const capOf = (i: Incident, config: DutyConfig): CapabilitySummary =>
  summariseCapability(i.resources, config.water.bulkWaterMinL);

const waterOf = (i: Incident, config: DutyConfig): WaterAssessment =>
  assessWater(i.waterPlan, i.resources, config);

interface BaShortfall {
  short: boolean;
  have: number;
  min: number;
  units: number;
  sourceTitle: string;
  standIn: boolean;
  /** True when the count came from a named roster (more precise than per-unit manual). */
  fromRoster: boolean;
}
function baShortfall(i: Incident, ctx: InsightContext): BaShortfall {
  const cap = capOf(i, ctx.config);
  const resolved = ctx.resolvedParams?.get('minBaOperators');
  const req = baOperatorRequirement(ctx.config, resolved);
  // Prefer roster-derived count when an integration roster is attached.
  const fromRoster = Boolean(ctx.roster);
  const have = ctx.roster ? rosterBAOperators(ctx.roster) : cap.baOperators;
  return {
    short: have < req.min,
    have,
    min: req.min,
    units: cap.unitCount,
    sourceTitle: req.source.title,
    standIn: req.source.standIn,
    fromRoster,
  };
}

interface CrewShortfall {
  short: boolean;
  have: number;
  units: number;
  /** True when the count came from a named roster (more precise than per-unit manual). */
  fromRoster: boolean;
}
/**
 * Total crew count — mirrors baShortfall's roster preference. There is no
 * doctrine-governed minimum crew count (unlike the 8-BA-operator
 * requirement), so "short" is simply zero-confirmed; a real threshold can be
 * added to DutyConfig/requirements the same way minBaOperators was, once
 * doctrine specifies one.
 */
function crewShortfall(i: Incident, ctx: InsightContext): CrewShortfall {
  const cap = capOf(i, ctx.config);
  const fromRoster = Boolean(ctx.roster);
  const have = ctx.roster ? rosterCrewCount(ctx.roster) : cap.crewCount;
  return { short: have === 0, have, units: cap.unitCount, fromRoster };
}

/**
 * Build index-aligned suggestedActions/actionSpecs, keeping only entries whose
 * `when` condition is true. Filtering both arrays from the same source (rather
 * than filtering each independently) guarantees they never fall out of sync.
 */
function pickActions(
  entries: ReadonlyArray<{ when: boolean; label: string; spec?: ActionSpec | null }>,
): { suggestedActions: string[]; actionSpecs: (ActionSpec | null)[] } {
  const kept = entries.filter((e) => e.when);
  return {
    suggestedActions: kept.map((e) => e.label),
    actionSpecs: kept.map((e) => e.spec ?? null),
  };
}

// --- type shorthands ---------------------------------------------------------

const FIRE_TYPES: IncidentType[] = ['StructureFire', 'GrassFire', 'VehicleFire'];

/** Support tags that represent water/logistics capacity — worth revisiting once fire status shows control achieved. */
const WATER_LOGISTICS_SUPPORT_TAGS = ['additional-tanker', 'bulk-water'] as const;

// --- rules -------------------------------------------------------------------

export const RULES: RuleDef[] = [
  // ── P1: Firefighter safety ─────────────────────────────────────────────────
  {
    id: 'strategy-capability-conflict',
    category: 'Strategy',
    incidentTypes: FIRE_TYPES,
    evaluate: (ctx) => {
      const { incident, config } = ctx;
      const strat = incident.itasc.actions.strategy;
      const offensiveInternal = strat === 'offensive-internal' || action(incident, 'offensive-internal');
      if (!offensiveInternal) return null;
      const ba = baShortfall(incident, ctx);
      const water = waterOf(incident, config);
      const cap = capOf(incident, config);
      const crew = crewShortfall(incident, ctx);
      const noPumper = cap.pumpers === 0;
      if (!ba.short && !water.limited && !crew.short && !noPumper) return null;
      const { suggestedActions, actionSpecs } = pickActions([
        { when: ba.short, label: 'Confirm BA / entry control' },
        { when: crew.short, label: 'Confirm crew numbers', spec: { type: 'open-panel', panel: 'resources' } },
        { when: noPumper, label: 'Confirm pumper', spec: { type: 'open-panel', panel: 'resources' } },
        { when: water.limited, label: 'Confirm water supply', spec: { type: 'open-panel', panel: 'water' } },
        { when: true, label: 'Switch to defensive', spec: { type: 'set-strategy', value: 'defensive-external' } },
      ]);
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'high',
        confidence: incident.resources.length > 0 ? 'high' : 'medium',
        title: 'Strategy / capability conflict',
        message:
          'Offensive internal operations appear to rely on capability not yet confirmed. Consider confirming capability or maintaining a defensive posture until confirmed.',
        whyShown: [
          'Strategy: offensive internal',
          ba.short ? `BA operators ${ba.have}/${ba.min}` : '',
          crew.short ? 'No crew confirmed' : '',
          noPumper ? 'No pumper among responding units' : '',
          water.limited ? 'Sustained water supply looks limited' : '',
        ].filter(Boolean),
        suggestedActions,
        actionSpecs,
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'ba-shortfall',
    category: 'Resource',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: (ctx) => {
      const { incident } = ctx;
      if (!confirmed(incident)) return null;
      const ba = baShortfall(incident, ctx);
      if (!ba.short) return null;
      const haveLabel = ba.fromRoster
        ? `${ba.have} named (from roster)`
        : ba.units > 0
          ? `${ba.have} confirmed`
          : 'none yet confirmed';
      const sourceLabel = ba.standIn ? `${ba.sourceTitle} ⚠ stand-in` : ba.sourceTitle;
      return {
        category: 'Resource',
        operationalPriority: 1,
        severity: 'high',
        confidence: ba.fromRoster || ba.units > 0 ? 'high' : 'low',
        title: 'BA capability appears short of guidance',
        message:
          `Guidance indicates a minimum of ${ba.min} BA operators for a structure fire; ${haveLabel} across ${ba.units} responding appliance(s). Consider additional brigade / appliance to meet the requirement.`,
        whyShown: [
          `Requirement: min ${ba.min} BA operators (${sourceLabel})`,
          ba.fromRoster
            ? `Named BA operators in roster: ${ba.have}`
            : `Confirmed across responding units: ${ba.have}`,
        ],
        suggestedActions: ['Confirm BA operator numbers', 'Request additional brigade / appliance'],
        actionSpecs: [null, { type: 'open-panel', panel: 'resources' }],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: ctx.resolvedParams?.get('minBaOperators')?.requirementId ?? 'SOP-SF-STANDIN-001', title: ba.sourceTitle }],
      };
    },
  },

  // ── P2: Safety of others ───────────────────────────────────────────────────
  {
    id: 'life-risk-unresolved',
    category: 'Life',
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !lifeUnresolved(incident)) return null;
      return {
        category: 'Life',
        operationalPriority: 2,
        severity: 'critical',
        confidence: 'medium',
        title: 'Life risk unresolved',
        message:
          'Occupancy appears unconfirmed for a confirmed structure fire. Consider confirming persons accounted for and ensuring an appropriate medical response.',
        whyShown: ['Confirmed structure fire', 'Occupancy unknown / unconfirmed'],
        suggestedActions: [
          'People accounted for',
          'Building clear',
          requested(incident, 'ambulance') ? 'Confirm Ambulance ETA' : 'Consider Ambulance',
        ],
        actionSpecs: [
          { type: 'set-life-status', value: 'accounted-for' },
          { type: 'set-life-status', value: 'building-clear' },
          null,
        ],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'confirm-incident-status',
    category: 'Workflow',
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      if (!isAtOrAfter(incident.stage, 'Arrival') || incident.status !== 'unconfirmed') return null;
      return {
        category: 'Workflow',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'high',
        title: 'Confirm incident status',
        message:
          'On arrival, appears not yet confirmed. Consider confirming whether this is a working structure fire or another outcome.',
        whyShown: ['Stage at or past Arrival', 'Status still unconfirmed'],
        suggestedActions: ['Confirm working fire / other outcome', 'Update first ITASC'],
        actionSpecs: [null, { type: 'open-panel', panel: 'itasc' }],
        requiresAcknowledgement: true,
      };
    },
  },

  // ── P3: Protection of property and environment ─────────────────────────────
  {
    id: 'limited-water-supply',
    category: 'Water',
    incidentTypes: FIRE_TYPES,
    evaluate: ({ incident, config }) => {
      if (!confirmed(incident)) return null;
      const water = waterOf(incident, config);
      if (!water.limited) return null;
      const hydrantNote = water.hydrantKnown
        ? `Nearest hydrant ${incident.waterPlan.nearestHydrantDistanceM} m (beyond ${config.water.hydrantConcernDistanceM} m)`
        : 'Nearest hydrant distance unknown';
      return {
        category: 'Water',
        operationalPriority: 3,
        severity: 'high',
        confidence: water.hydrantKnown && incident.resources.length > 0 ? 'high' : 'medium',
        title: 'Limited water supply likely',
        message:
          'Sustained structural firefighting water looks limited. Consider tanker / static water, relay / shuttle, or a strategy adjustment.',
        whyShown: [
          'Confirmed structure fire',
          hydrantNote,
          `~${water.onboardWaterL} L onboard across responding units`,
          'No bulk water assigned',
        ],
        suggestedActions: ['Consider additional tanker / bulk water', 'Update water plan', 'Identify static water'],
        actionSpecs: [null, { type: 'open-panel', panel: 'water' }, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'resource-adequacy-check',
    category: 'Resource',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: (ctx) => {
      const { incident, config } = ctx;
      if (!confirmed(incident)) return null;
      const cap = capOf(incident, config);
      const noPumper = cap.pumpers === 0;
      const crew = crewShortfall(incident, ctx);
      if (!noPumper && !crew.short) return null;
      const crewNote = crew.short
        ? crew.fromRoster
          ? 'No crew confirmed (named roster shows none active)'
          : 'No crew confirmed'
        : '';
      const { suggestedActions, actionSpecs } = pickActions([
        { when: noPumper, label: 'Confirm pumper' },
        { when: crew.short, label: 'Confirm crew numbers' },
        { when: true, label: 'Update responding resources', spec: { type: 'open-panel', panel: 'resources' } },
      ]);
      return {
        category: 'Resource',
        operationalPriority: 3,
        severity: 'high',
        confidence: cap.unitCount > 0 ? 'medium' : 'low',
        title: 'Resource adequacy check',
        message:
          'For a confirmed structure fire, core resources appear unconfirmed. Consider confirming a suitable pumper and crew.',
        whyShown: [
          'Confirmed structure fire',
          noPumper ? 'No pumper among responding units' : '',
          crewNote,
          `${cap.unitCount} responding unit(s)`,
        ].filter(Boolean),
        suggestedActions,
        actionSpecs,
      };
    },
  },
  {
    id: 'mixed-rfs-frnsw-coordination',
    category: 'Coordination',
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      const rfsLead = /rfs/i.test(incident.leadAgency);
      const frnsw = incident.supportingAgencies.some((a) => /frnsw/i.test(a)) || requested(incident, 'frnsw');
      if (!rfsLead || !frnsw || !isAtOrAfter(incident.stage, 'Arrival')) return null;
      return {
        category: 'Coordination',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'high',
        title: 'Mixed RFS / FRNSW coordination',
        message:
          'Both agencies are involved. Consider confirming IC, radio channel, staging, FRNSW pumper tasking, BA/entry-control arrangements and water-supply responsibilities.',
        whyShown: ['RFS primary', 'FRNSW attending / requested'],
        suggestedActions: ['Confirm IC & channel', 'Confirm staging', 'Confirm pumper tasking'],
        actionSpecs: [null, null, null],
      };
    },
  },
  {
    id: 'exposure-extension-risk',
    category: 'Exposure',
    incidentTypes: FIRE_TYPES,
    evaluate: ({ incident }) => {
      const exposure = threat(incident, 'exposures');
      const extension = threat(incident, 'grass-bush-extension');
      if (!exposure && !extension) return null;
      return {
        category: 'Exposure',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'medium',
        title: 'Exposure / extension risk',
        message:
          'Exposures or fire extension are flagged. Consider exposure protection and confirming the fire is not extending to adjacent structures or vegetation.',
        whyShown: [exposure ? 'Exposure flagged' : '', extension ? 'Grass/bush extension flagged' : ''].filter(Boolean),
        suggestedActions: ['Consider exposure protection', 'Confirm no extension'],
        actionSpecs: [null, null],
      };
    },
  },
  {
    id: 'access-risk',
    category: 'Access',
    evaluate: ({ incident, spatial }) => {
      const flagged = threat(incident, 'access');
      const notes = spatial?.accessNotes ?? [];
      if (!flagged && notes.length === 0) return null;
      const whyShown = [flagged ? 'Access flagged as a threat' : ''].filter(Boolean);
      for (const n of notes.slice(0, 3)) whyShown.push(`Local knowledge: ${n}`);
      return {
        category: 'Access',
        operationalPriority: 3,
        severity: 'medium',
        confidence: notes.length > 0 ? 'high' : 'medium',
        title: 'Access risk / local knowledge',
        message:
          notes.length > 0
            ? 'Local knowledge indicates access constraints near this address. Consider confirming approach, staging and route before committing appliances.'
            : 'Access is flagged as a threat. Consider confirming approach, staging and any local access constraints (gates, bridges, narrow driveways).',
        whyShown,
        suggestedActions: ['Confirm approach & staging', 'Brief incoming appliances on access'],
        actionSpecs: [null, null],
      };
    },
  },
  {
    id: 'static-water-opportunity',
    category: 'Water',
    incidentTypes: FIRE_TYPES,
    evaluate: ({ incident, config }) => {
      const water = waterOf(incident, config);
      if (!confirmed(incident) || !water.limited || water.staticWaterAvailable) return null;
      return {
        category: 'Water',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'low',
        title: 'Static water opportunity',
        message:
          'With reticulated supply limited, consider identifying and confirming nearby static water (dam / tank / pool) and its access/suitability.',
        whyShown: ['Sustained water supply limited', 'No static water confirmed'],
        suggestedActions: ['Identify static water source', 'Confirm access & suitability', 'Update water plan'],
        actionSpecs: [null, null, { type: 'open-panel', panel: 'water' }],
      };
    },
  },

  // ── P4: Restoration of normality ───────────────────────────────────────────
  {
    id: 'first-itasc-pending',
    category: 'Workflow',
    evaluate: ({ incident }) => {
      const beforeArrival = stageIndex(incident.stage) <= stageIndex('Arrival');
      if (!beforeArrival || incident.itasc.command.controlTaken) return null;
      return {
        category: 'Workflow',
        operationalPriority: 4,
        severity: 'info',
        confidence: 'high',
        title: 'First ITASC pending',
        message:
          'Consider preparing the first arriving ITASC. Confirm incident, life risk, water and initial strategy on arrival.',
        whyShown: ['No control taken yet', `Stage: ${incident.stage}`],
        suggestedActions: ['Prepare first ITASC', 'Confirm incident on arrival'],
        actionSpecs: [{ type: 'open-panel', panel: 'itasc' }, null],
      };
    },
  },
  {
    id: 'overhaul-investigation',
    category: 'Investigation',
    minStage: 'Overhaul',
    incidentTypes: ['StructureFire', 'VehicleFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      const inOverhaul = isAtOrAfter(incident.stage, 'Overhaul') || action(incident, 'overhaul');
      if (!inOverhaul) return null;
      return {
        category: 'Investigation',
        operationalPriority: 4,
        severity: 'medium',
        confidence: 'medium',
        title: 'Overhaul / evidence preservation',
        message:
          'Transitioning to overhaul. Before disturbing the scene beyond what is needed for safety and extinguishment, consider hidden-fire checks, utilities isolation, evidence preservation and the investigation pathway.',
        whyShown: ['Stage at or past Overhaul'],
        suggestedActions: ['Hidden fire / TIC check', 'Isolate utilities', 'Preserve area of origin', 'Notify investigation'],
        actionSpecs: [null, null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'GL-OVERHAUL-001', title: 'Overhaul & fire investigation / evidence preservation (stand-in)' }],
      };
    },
  },
  {
    id: 'fire-status-stale-in-overhaul',
    category: 'Workflow',
    incidentTypes: FIRE_TYPES,
    minStage: 'Overhaul',
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || incident.itasc.incident.fireStatus) return null;
      return {
        category: 'Workflow',
        operationalPriority: 4,
        severity: 'info',
        confidence: 'high',
        title: 'Fire status not yet recorded',
        message:
          'Now in overhaul with no fire status recorded. Consider setting one (e.g. contained, under control) for handover and SITREP clarity.',
        whyShown: ['Stage at or past Overhaul', 'Fire status not set'],
        suggestedActions: ['Record fire status'],
        actionSpecs: [{ type: 'open-panel', panel: 'itasc' }],
      };
    },
  },
  {
    id: 'fire-status-resource-review',
    category: 'Water',
    incidentTypes: FIRE_TYPES,
    minStage: 'Suppression',
    evaluate: ({ incident }) => {
      const fireStatus = incident.itasc.incident.fireStatus;
      if (!confirmed(incident) || !fireStatus || !FIRE_STATUS_CONTROLLED.includes(fireStatus)) return null;
      const waterRequested = incident.itasc.support.requested.filter((s) =>
        (WATER_LOGISTICS_SUPPORT_TAGS as readonly string[]).includes(s),
      );
      const gaps = incident.itasc.support.unresolvedGaps;
      if (waterRequested.length === 0 && gaps.length === 0) return null;
      return {
        category: 'Water',
        operationalPriority: 4,
        severity: 'info',
        confidence: 'medium',
        title: 'Review support requests as fire status improves',
        message:
          `Fire status shows ${FIRE_STATUS_LABELS[fireStatus].toLowerCase()}. Consider reviewing whether outstanding water/logistics support requests are still warranted, or can be released for other taskings.`,
        whyShown: [
          `Fire status: ${FIRE_STATUS_LABELS[fireStatus]}`,
          waterRequested.length > 0 ? `Water support requested: ${waterRequested.map(labelForTag).join(', ')}` : '',
          gaps.length > 0 ? `Outstanding: ${gaps.map(labelForTag).join(', ')}` : '',
        ].filter(Boolean),
        suggestedActions: ['Review support requests'],
        actionSpecs: [{ type: 'open-panel', panel: 'itasc' }],
      };
    },
  },

  // ── Structural fireground (expanded) ───────────────────────────────────────
  {
    id: 'fire-behaviour-warning-signs',
    category: 'Strategy',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !threat(incident, 'fire-behaviour-signs')) return null;
      const offensive =
        incident.itasc.actions.strategy === 'offensive-internal' || action(incident, 'offensive-internal');
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'medium',
        title: 'Fire behaviour warning signs flagged',
        message:
          'Fire behaviour indicators consistent with a deteriorating or ventilation-limited fire (e.g. rollover, high heat with poor visibility, pressurised smoke, rapid air movement into an opening) are flagged. Consider reassessing conditions before committing or continuing interior entry, and whether a defensive posture is more appropriate until conditions are reassessed.',
        whyShown: [
          'Fire behaviour warning signs flagged',
          offensive ? 'Strategy: offensive internal / entry in progress' : '',
        ].filter(Boolean),
        suggestedActions: [
          'Reassess conditions before entry / continuing entry',
          'Brief crew on observed fire behaviour',
          offensive ? 'Consider defensive posture until reassessed' : 'Confirm strategy remains appropriate',
        ],
        actionSpecs: [null, null, offensive ? { type: 'set-strategy', value: 'defensive-external' } : null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'solar-battery-energised-hazard',
    category: 'Life',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !threat(incident, 'solar-batteries')) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'Solar PV / battery storage hazard flagged',
        message:
          'Rooftop solar and/or battery storage is flagged. Rooftop PV panels remain energised in daylight even with mains power isolated at the switchboard, and battery storage systems can retain stranded energy / carry a thermal-runaway and reignition risk. Consider confirming isolation with a competent person (installer/network operator) before working near the array or inverter, maintaining safe standoff when directing water near panels, and treating DC wiring as energised until confirmed otherwise.',
        whyShown: ['Solar / battery storage flagged as a threat', 'Confirmed structure fire'],
        suggestedActions: [
          'Confirm isolation with competent person / network operator',
          'Maintain safe standoff from panels/inverter when directing water',
          'Brief crew: mains-off does not de-energise rooftop PV',
        ],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'GL-SOLAR-BESS-001', title: 'Solar PV / battery storage — fireground hazard guidance (stand-in)' }],
      };
    },
  },
  {
    id: 'ventilation-coordination-check',
    category: 'Strategy',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !action(incident, 'ventilation')) return null;
      const attackReady =
        action(incident, 'establishing-water') || action(incident, 'offensive-internal') || action(incident, 'first-attack');
      if (attackReady) return null;
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'low',
        title: 'Ventilation — confirm coordination with attack',
        message:
          'Ventilation is selected without a confirmed established water supply or attack line recorded. Consider confirming ventilation is coordinated with a ready, positioned attack line before opening the structure — uncoordinated ventilation can accelerate fire growth.',
        whyShown: ['Ventilation action selected', 'No established-water / attack action recorded yet'],
        suggestedActions: ['Confirm attack line ready before ventilating', 'Update actions once water/attack is established'],
        actionSpecs: [null, { type: 'open-panel', panel: 'itasc' }],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'ba-entry-control-not-confirmed',
    category: 'Resource',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: (ctx) => {
      const { incident } = ctx;
      if (!confirmed(incident)) return null;
      const offensiveInternal =
        incident.itasc.actions.strategy === 'offensive-internal' || action(incident, 'offensive-internal');
      const ba = baShortfall(incident, ctx);
      const baCommitted = ba.have > 0;
      if (!offensiveInternal && !baCommitted) return null;
      if (action(incident, 'ba-entry-control')) return null;
      return {
        category: 'Resource',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'BA entry control not yet confirmed',
        message:
          'BA operators appear committed or offensive internal operations are underway, but entry control does not yet appear confirmed. Consider establishing an entry-control point tracking who is inside, cylinder pressure and entry time before further BA-supported entry.',
        whyShown: [
          offensiveInternal ? 'Strategy: offensive internal' : '',
          baCommitted ? `BA operators committed: ${ba.have}` : '',
          'Entry control not recorded as established',
        ].filter(Boolean),
        suggestedActions: ['Confirm entry control point established', 'Record cylinder pressures / entry time'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'SOP-SF-STANDIN-001', title: 'NSW structure-fire BA guidance (stand-in)' }],
      };
    },
  },
  {
    id: 'wui-structure-defensibility',
    category: 'Exposure',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !threat(incident, 'grass-bush-extension')) return null;
      const defensive = incident.itasc.actions.strategy === 'defensive' || action(incident, 'defensive');
      return {
        category: 'Exposure',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'Structure fire with active vegetation-fire extension',
        message:
          'This structure fire is flagged with grass/bush fire extension. Beyond exposure protection, consider firefighter safety and structure defensibility specifically — escape routes, a safety zone / temporary refuge, and whether conditions still support defending this structure or whether withdrawal should be considered.',
        whyShown: ['Grass/bush extension flagged on a confirmed structure fire', defensive ? 'Strategy: defensive' : ''].filter(Boolean),
        suggestedActions: ['Confirm escape route / safety zone', 'Reassess structure defensibility given fire behaviour', 'Brief crew on withdrawal trigger points'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'structural-collapse-awareness',
    category: 'Exposure',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !threat(incident, 'collapse')) return null;
      return {
        category: 'Exposure',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'low',
        title: 'Structural collapse risk flagged',
        message:
          'Structural collapse risk is flagged. Consider establishing and marking a collapse zone, being alert to indicators such as visible sagging, cracking, unusual noises or prior fire damage/duration of burn to load-bearing elements, and limiting interior crews/time where risk is elevated.',
        whyShown: ['Collapse flagged as a threat', 'Confirmed structure fire'],
        suggestedActions: ['Establish / mark collapse zone', 'Brief crew on collapse indicators', 'Limit interior exposure where risk is elevated'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'asbestos-precaution',
    category: 'Exposure',
    incidentTypes: ['StructureFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'asbestos')) return null;
      return {
        category: 'Exposure',
        operationalPriority: 2,
        severity: 'medium',
        confidence: 'medium',
        title: 'Asbestos flagged — precautions',
        message:
          'Asbestos-containing material is flagged as possibly present. Consider minimising disturbance of debris, keeping affected material wet where practicable (avoiding high-pressure spray directly onto it), appropriate respiratory protection (P2) for anyone working near debris, and isolating/signing the affected area. This is a safety precaution, not a compliance determination — notify the appropriate authority for assessment.',
        whyShown: ['Asbestos flagged as a threat'],
        suggestedActions: ['Minimise debris disturbance', 'Keep affected material wet (avoid high-pressure spray)', 'Isolate / sign affected area', 'Consider P2 respirator for work near debris'],
        actionSpecs: [null, null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'GL-ASBESTOS-001', title: 'Asbestos in fire-damaged buildings — precautions (stand-in)' }],
      };
    },
  },
  {
    id: 'salvage-consideration',
    category: 'Workflow',
    incidentTypes: ['StructureFire'] as IncidentType[],
    minStage: 'Suppression',
    evaluate: ({ incident }) => {
      if (!confirmed(incident) || !isAtOrAfter(incident.stage, 'Suppression')) return null;
      if (action(incident, 'salvage')) return null;
      return {
        category: 'Workflow',
        operationalPriority: 3,
        severity: 'info',
        confidence: 'medium',
        title: 'Consider salvage alongside suppression',
        message:
          'Consider salvage actions alongside suppression / overhaul — e.g. moving or covering contents threatened by fire, smoke or water, controlling water runoff, and minimising water damage where it does not compromise extinguishment or safety.',
        whyShown: ['Stage at or past Suppression', 'Salvage not yet recorded'],
        suggestedActions: ['Consider salvage of threatened contents', 'Control water runoff where practicable'],
        actionSpecs: [null, null],
        doctrineRefs: [{ docId: 'GL-SALVAGE-001', title: 'Salvage activities (stand-in)' }],
      };
    },
  },

  // ── Bush / grass / forest fire rules ────────────────────────────────────────
  {
    id: 'bushfire-laces-check',
    category: 'Strategy',
    incidentTypes: ['GrassFire'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      const committed =
        action(incident, 'direct-attack') || action(incident, 'indirect-attack') || action(incident, 'backburning');
      if (!confirmed(incident) || !committed) return null;
      const tactics = [
        action(incident, 'direct-attack') ? 'direct attack' : '',
        action(incident, 'indirect-attack') ? 'indirect attack' : '',
        action(incident, 'backburning') ? 'backburning' : '',
      ].filter(Boolean).join(', ');
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'high',
        title: 'Confirm LACES before committing crews to the fire edge',
        message:
          'Crews appear to be working the fire edge. Consider confirming Lookouts, Awareness of current/forecast fire behaviour, Communications, Escape routes and Safety zones (LACES) are in place, and re-briefing them at every change of task or location.',
        whyShown: ['Incident type: Grass / bush fire', `Fire-edge tactic selected: ${tactics}`, `Stage: ${incident.stage}`],
        suggestedActions: ['Confirm lookouts posted', 'Confirm escape routes & safety zones', 'Brief crew on current & forecast fire behaviour'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'GL-BF-LACES-001', title: 'LACES fireground safety system — bush/grass fire operations (stand-in)' }],
      };
    },
  },
  {
    id: 'bushfire-wind-change-danger',
    category: 'Strategy',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'wind-change')) return null;
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'medium',
        title: 'Wind change flagged — reassess crew positions',
        message:
          'A wind change is flagged. Consider that a flank can rapidly become the new head of the fire — reassess LACES (especially escape routes and safety zones) for every crew relative to the new predicted fire direction before it arrives, not after.',
        whyShown: ['Wind change flagged as a threat'],
        suggestedActions: ['Reassess crew positions relative to new fire direction', 'Confirm escape routes still valid under new wind', 'Consider withdrawing crews from exposed flanks'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'GL-BF-LACES-001', title: 'LACES fireground safety system — bush/grass fire operations (stand-in)' }],
      };
    },
  },
  {
    id: 'backburn-asset-protection-conflict',
    category: 'Strategy',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      const backburning = action(incident, 'backburning');
      if (!backburning) return null;
      const assetProtection =
        action(incident, 'exposure-protection') || incident.itasc.actions.strategy === 'exposure-protection' || threat(incident, 'exposures');
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'medium',
        title: 'Backburning selected — confirm authorisation and risk/benefit',
        message:
          'Backburning is selected' +
          (assetProtection ? ' alongside asset/exposure protection' : '') +
          '. Backburning carries a materially higher risk profile than direct attack — consider confirming it has been explicitly authorised at the appropriate command level, that a risk-versus-benefit assessment has been made for current and forecast conditions, and that crews and assets in the backburn\'s intended path (including any undertaking exposure protection nearby) are clear or briefed before ignition.',
        whyShown: [
          'Action: Backburning selected',
          assetProtection ? 'Exposure/asset protection also flagged or in progress nearby' : '',
          'Backburn authorisation and risk/benefit assessment are not yet tracked as structured ITASC fields — confirm and record manually',
        ].filter(Boolean),
        suggestedActions: [
          'Confirm backburn authorised at appropriate command level',
          'Confirm risk-vs-benefit assessed for current conditions',
          'Confirm crews/assets in backburn path are clear or briefed',
          'Record authorisation and rationale in command notes',
        ],
        actionSpecs: [null, null, null, { type: 'open-panel', panel: 'itasc' }],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'OPG-BF-BACKBURN-001', title: 'Backburning — authorisation and risk/benefit assessment (stand-in)' }],
      };
    },
  },
  {
    id: 'bushfire-extreme-fire-danger',
    category: 'Strategy',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'extreme-fire-danger')) return null;
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'high',
        title: 'Extreme / Catastrophic fire danger flagged',
        message:
          'Extreme or Catastrophic fire danger conditions are flagged for today. Consider a wider safety margin on LACES elements than usual (larger safety zones, earlier trigger points for withdrawal) and whether direct attack is appropriate at all in these conditions.',
        whyShown: ['Extreme / Catastrophic fire danger flagged'],
        suggestedActions: ['Reassess LACES margins for today’s conditions', 'Consider indirect attack / defensive posture only', 'Confirm crew and any community warnings reflect today’s rating'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'GL-BF-AFDRS-001', title: 'Australian Fire Danger Rating System — operational categories (stand-in)' }],
      };
    },
  },
  {
    id: 'bushfire-crown-fire-risk',
    category: 'Strategy',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'crown-fire')) return null;
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'medium',
        title: 'Crown fire flagged — forest fuel behaves differently',
        message:
          'Fire is flagged as running in the tree crowns. Consider that crown fire spreads faster and spots further than surface/grass fire, that direct ground attack at the head is unlikely to be safe or effective, and reassessing LACES with an increased margin for this sector.',
        whyShown: ['Crown fire flagged (forest fuel)'],
        suggestedActions: ['Consider indirect attack ahead of the fire rather than direct', 'Watch for increased spotting distance downwind', 'Consider aircraft support for this sector'],
        actionSpecs: [null, null, { type: 'open-panel', panel: 'resources' }],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'bushfire-remote-area-terrain',
    category: 'Access',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'difficult-terrain')) return null;
      return {
        category: 'Access',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'Difficult terrain — confirm ground-crew escape route',
        message:
          'Difficult or remote terrain is flagged. Consider whether ground crews in this sector have a vehicle-accessible escape route, or whether the sector needs Remote Area Firefighting Team (RAFT) and/or aircraft insertion rather than appliance-based crews.',
        whyShown: ['Difficult / remote terrain flagged'],
        suggestedActions: ['Confirm ground crews have a vehicle-accessible escape route', 'Consider RAFT / aircraft insertion for this sector', 'Brief crew on terrain-specific hazards'],
        actionSpecs: [null, { type: 'open-panel', panel: 'resources' }, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'bushfire-ember-spotting-risk',
    category: 'Exposure',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      const ember = threat(incident, 'ember-attack');
      const spotting = threat(incident, 'spot-fires');
      if (!ember && !spotting) return null;
      return {
        category: 'Exposure',
        operationalPriority: 2,
        severity: spotting ? 'critical' : 'high',
        confidence: 'medium',
        title: spotting ? 'Spot fires confirmed outside containment' : 'Ember attack risk flagged',
        message: spotting
          ? 'Spot fires are flagged outside the containment line. Consider that the fire may be outrunning the current plan — check for further spotting downwind, and reassess whether exposed structures ahead of the main front (not just alongside it) need protection now.'
          : 'Ember attack risk is flagged. Consider that embers can start new fires well ahead of the visible front — check structures and vegetation downwind that are not yet directly exposed to flame.',
        whyShown: [spotting ? 'Spot fires flagged' : '', ember ? 'Ember attack risk flagged' : ''].filter(Boolean),
        suggestedActions: ['Check for spot fires downwind / ahead of containment lines', 'Reassess exposed structures not yet in the direct path', 'Consider additional crew for exposure protection'],
        actionSpecs: [null, null, { type: 'open-panel', panel: 'resources' }],
        requiresAcknowledgement: spotting,
      };
    },
  },
  {
    id: 'bushfire-aircraft-ground-safety',
    category: 'Coordination',
    incidentTypes: ['GrassFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!requested(incident, 'aircraft')) return null;
      return {
        category: 'Coordination',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'Aircraft tasked — confirm ground/air deconfliction',
        message:
          'Aircraft support is requested/tasked. Consider confirming ground crews are clear of intended drop/bombing lines before each pass, that a shared air-ground contact frequency is established, and — for larger taskings — that an Air Base Manager / Air Operations Manager function is coordinating aircraft rather than ground crews self-tasking directly.',
        whyShown: ['Aircraft support requested'],
        suggestedActions: ['Confirm crews clear of drop lines before each pass', 'Confirm air-ground contact frequency', 'Confirm Air Base / Air Operations Manager coordinating tasking'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        // No sufficiently-confirmed NSW-specific source for a dedicated corpus entry this pass
        // (trade/regional press only, see the bushfire research brief) — omit doctrineRefs rather
        // than cite a document that doesn't exist in the corpus.
      };
    },
  },
  {
    id: 'bushfire-s44-escalation-consideration',
    category: 'Coordination',
    incidentTypes: ['GrassFire'] as IncidentType[],
    minStage: 'Suppression',
    evaluate: ({ incident }) => {
      const bigResponse = incident.level === 'Level2' || incident.level === 'Level3' || incident.level === 'IMT';
      const stillGoing = !incident.itasc.incident.fireStatus || incident.itasc.incident.fireStatus === 'going';
      if (!bigResponse || !stillGoing) return null;
      return {
        category: 'Coordination',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'low',
        title: 'Consider whether wider escalation is warranted',
        message:
          'This is a going fire at an elevated response level. Consider whether escalation beyond local resources — including a Rural Fires Act 1997 (NSW) s44 declaration, which is a Commissioner-level decision, not one this app makes — may be warranted, and that the duty officer / district is aware of current status and resourcing.',
        whyShown: [`Response level: ${incident.level}`, `Fire status: ${incident.itasc.incident.fireStatus ?? 'not yet recorded'}`],
        suggestedActions: ['Confirm duty officer / district aware of status', 'Consider whether resourcing needs exceed local capacity'],
        actionSpecs: [null, null],
        doctrineRefs: [{ docId: 'REF-BF-S44-001', title: 'Rural Fires Act 1997 (NSW) s44 — Commissioner’s responsibility (reference, real legislation)' }],
      };
    },
  },

  // ── MVA / rescue rules ─────────────────────────────────────────────────────
  {
    id: 'mva-scene-safety',
    category: 'Life',
    incidentTypes: ['MVA'] as IncidentType[],
    evaluate: ({ incident }) => {
      const approachStages = ['Dispatch', 'EnRoute'] as const;
      if (!approachStages.includes(incident.stage as typeof approachStages[number])) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'high',
        title: 'MVA — approach and scene safety',
        message:
          'Approaching MVA. Consider traffic management requirements, hazard assessment (fuel/battery/SRS/stability) and confirming safe approach lane and parking before committing crew.',
        whyShown: ['Incident type: MVA / rescue', `Stage: ${incident.stage}`],
        suggestedActions: [
          'Consider Police for traffic management',
          'Brief crew on hazards before approach',
        ],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'mva-casualty-assessment',
    category: 'Life',
    incidentTypes: ['MVA'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      const life = incident.itasc.threats.life?.trim();
      const lifeUnknown = !life || /unknown|unconfirmed|tbc|nil known/i.test(life);
      if (!lifeUnknown) return null;
      return {
        category: 'Life',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'high',
        title: 'MVA — casualty count not confirmed',
        message:
          'Casualties not yet confirmed. Consider rapid scene size-up and DRA, confirming number of patients and entrapment status, and requesting Ambulance if not already en route.',
        whyShown: ['MVA — arrival stage or later', 'Life threat entry not completed'],
        suggestedActions: [
          requested(incident, 'ambulance') ? 'Confirm Ambulance ETA' : 'Request Ambulance',
          'Confirm casualty count',
          'Confirm entrapment status',
        ],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'mva-rescue-resource',
    category: 'Resource',
    incidentTypes: ['MVA'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      const rescueSelected = action(incident, 'rescue-search');
      const frnsw =
        incident.supportingAgencies.some((a) => /frnsw/i.test(a)) ||
        requested(incident, 'frnsw');
      if (!rescueSelected || frnsw) return null;
      return {
        category: 'Resource',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'medium',
        title: 'MVA rescue — FRNSW rescue capability',
        message:
          'Rescue actions are selected but FRNSW rescue is not yet confirmed. For vehicle extrication, FRNSW rescue capability may be required.',
        whyShown: ['MVA with rescue actions selected', 'FRNSW rescue not yet confirmed'],
        suggestedActions: ['Consider FRNSW rescue request', 'Confirm extrication requirement'],
        actionSpecs: [null, null],
      };
    },
  },
  {
    id: 'mva-fuel-spill-hazmat',
    category: 'Strategy',
    incidentTypes: ['MVA'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'fuel-spill')) return null;
      return {
        category: 'Strategy',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'medium',
        title: 'MVA — fuel spill',
        message:
          'A fuel spill is flagged. Consider isolating ignition sources, confirming absorbent/containment on scene, and whether the spill is significant or ongoing enough to warrant EPA / hazmat notification.',
        whyShown: ['MVA with fuel spill flagged'],
        suggestedActions: ['Isolate ignition sources', 'Consider EPA / hazmat if spill is significant'],
        actionSpecs: [null, null],
      };
    },
  },
  {
    id: 'mva-multiple-vehicles-resource',
    category: 'Resource',
    incidentTypes: ['MVA'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      if (!threat(incident, 'multiple-vehicles')) return null;
      return {
        category: 'Resource',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'medium',
        title: 'MVA — multiple vehicles involved',
        message:
          'Multiple vehicles are flagged. Consider confirming the total casualty count across all vehicles (not just the first assessed) and whether additional ambulance or rescue resourcing is needed for the full scene.',
        whyShown: ['MVA with multiple vehicles flagged'],
        suggestedActions: ['Confirm total casualty count across all vehicles', 'Consider additional ambulance / rescue'],
        actionSpecs: [null, null],
      };
    },
  },

  // ── Vehicle fire / EV / battery rules ──────────────────────────────────────
  // Vehicle-context rules (approach, recognition, extrication caution,
  // reclassification) are scoped to ['MVA', 'VehicleFire']. Hazard-itself
  // rules (stranded energy, thermal runaway, suppression strategy, water
  // demand, reignition monitoring) are deliberately type-agnostic so they
  // also fire for a StructureFire/Other job where an officer flags
  // ev-hv-battery/thermal-runaway on a home-garage or charging-equipment fire
  // (a real, documented NSW case — FRNSW-D25/20316's Berkeley Vale incident),
  // with zero registry changes needed for that to work.
  {
    id: 'vehicle-fire-approach-safety',
    category: 'Life',
    incidentTypes: ['MVA', 'VehicleFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      const showingFire = ['smoke', 'flame', 'smoke-and-flame'].includes(incident.itasc.incident.showing ?? '');
      const approachStages = ['Dispatch', 'EnRoute', 'Arrival'] as const;
      if (!showingFire || !approachStages.includes(incident.stage as typeof approachStages[number])) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'Vehicle fire — approach and positioning',
        message:
          'A vehicle fire appears involved. Consider a 45-degree upwind/uphill approach that avoids standing directly in line with tyres, bumpers or hatch struts (documented projectile risk under sustained heat), and confirming whether the vehicle may be electric/hybrid before committing further.',
        whyShown: ['Showing: fire/smoke on a vehicle incident', `Stage: ${incident.stage}`],
        suggestedActions: ['Approach at 45° upwind, avoid tyre/bumper/strut lines', 'Check for EV/hybrid indicators before committing'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'ev-recognition-check',
    category: 'Life',
    incidentTypes: ['MVA', 'VehicleFire'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      const relevantThreat =
        threat(incident, 'entrapment') ||
        threat(incident, 'fuel-spill') ||
        ['smoke', 'flame', 'smoke-and-flame'].includes(incident.itasc.incident.showing ?? '');
      const alreadyFlagged = threat(incident, 'ev-hv-battery');
      if (!relevantThreat || alreadyFlagged) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'Confirm whether this vehicle is electric/hybrid',
        message:
          'EV/hybrid status does not appear confirmed. Consider checking for a blue "EV" number-plate triangle, orange high-voltage cabling, a QR code (windscreen/door pillar/fuel cap), badging, or absence of an exhaust/grille before further extrication or suppression — the ANCAP RESCUE app or manufacturer emergency response guide can confirm the make/model.',
        whyShown: ['Vehicle incident with entrapment, fuel spill, or fire indicators', 'EV/HV battery not yet flagged as a threat'],
        suggestedActions: ['Check EV/HV indicators (plate, cabling, QR code, badging)', 'Consult ANCAP RESCUE app / manufacturer ERG'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'AFAC-3096', title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)' }],
      };
    },
  },
  {
    id: 'ev-stranded-energy-isolation',
    category: 'Life',
    evaluate: ({ incident }) => {
      if (!threat(incident, 'ev-hv-battery') && !threat(incident, 'stranded-energy')) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'high',
        title: 'HV battery — never cut orange cabling or the service plug',
        message:
          'An HV battery is flagged. Consider treating the high-voltage system as energised regardless of whether the vehicle appears "off" — never cut or pierce orange HV cabling, a sealed battery enclosure, or the service plug. Isolation is via the 12V system per the manufacturer emergency response guide, not by cutting into HV components.',
        whyShown: ['EV / HV battery flagged', 'Stranded energy cannot be visually ruled out even when the vehicle appears powered down'],
        suggestedActions: ['Isolate via 12V system per manufacturer ERG (not the service plug)', 'Treat orange cabling / battery enclosure as energised — do not cut or pierce'],
        actionSpecs: [{ type: 'open-panel', panel: 'itasc' }, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'AFAC-3096', title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)' }],
      };
    },
  },
  {
    id: 'ev-thermal-runaway-ppe-exclusion',
    category: 'Life',
    evaluate: ({ incident }) => {
      if (!threat(incident, 'thermal-runaway')) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'high',
        title: 'Battery thermal runaway — PPE and exclusion zone',
        message:
          'Signs of battery thermal runaway are flagged (venting/off-gassing, hissing/popping, jet-flame or a TIC hotspot). Consider full structural PPE with SCBA for anyone in proximity, atmospheric monitoring where available, an exclusion zone accounting for wind direction, and extreme caution before opening any compartment that may be holding accumulated flammable vapour (vapour-cloud explosion / extreme fire behaviour risk).',
        whyShown: ['Thermal runaway signs flagged'],
        suggestedActions: ['Confirm full PPE + SCBA in proximity', 'Establish exclusion zone (account for wind)', 'Avoid opening compartments that may hold accumulated vapour'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'AFAC-3096', title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)' }],
      };
    },
  },
  {
    id: 'ev-extrication-hv-caution',
    category: 'Life',
    incidentTypes: ['MVA', 'VehicleFire'] as IncidentType[],
    evaluate: ({ incident }) => {
      const extricating = action(incident, 'extrication') || threat(incident, 'entrapment');
      if (!extricating || !threat(incident, 'ev-hv-battery')) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'medium',
        title: 'EV extrication — visualise the cutting zone first',
        message:
          'Extrication is indicated on a vehicle with an HV battery flagged. Consider fully visualising and exposing the cutting area before starting to confirm there is no HV cabling or SRS component in the line of cut — standard techniques such as boot tunnelling, sill cuts and full crushes may not be safe on an EV given battery/cabling location. Refer to the ANCAP RESCUE app or manufacturer emergency response guide for this model before cutting, and if the vehicle is on its side, stabilise before cutting given the added rollover risk from battery weight.',
        whyShown: ['Extrication indicated', 'EV / HV battery flagged'],
        suggestedActions: ['Visualise/expose cutting area before cutting', 'Consult ANCAP RESCUE app / manufacturer ERG for this model'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'AFAC-3096', title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)' }],
      };
    },
  },
  {
    id: 'mva-fire-reclassification',
    category: 'Strategy',
    incidentTypes: ['MVA'] as IncidentType[],
    evaluate: ({ incident }) => {
      const showingFire = ['smoke', 'flame', 'smoke-and-flame'].includes(incident.itasc.incident.showing ?? '');
      const fireActionSelected = action(incident, 'first-attack') || action(incident, 'offensive-external') || action(incident, 'defensive');
      if (!showingFire || fireActionSelected) return null;
      return {
        category: 'Strategy',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'medium',
        title: 'MVA now showing fire — consider a suppression posture',
        message:
          'This was logged as an MVA but fire/smoke is now showing. Consider whether crew are equipped and postured for fire attack (not extrication alone) — structural PPE, BA availability and a suppression strategy — alongside any ongoing rescue/extrication task.',
        whyShown: [`Showing: ${incident.itasc.incident.showing}`, 'No fire-attack strategy/action recorded yet'],
        suggestedActions: ['Confirm suppression strategy and PPE/BA readiness', 'Update ITASC actions'],
        actionSpecs: [null, { type: 'open-panel', panel: 'itasc' }],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'ev-battery-suppression-strategy',
    category: 'Strategy',
    evaluate: ({ incident }) => {
      const fireConfirmed =
        ['smoke', 'flame', 'smoke-and-flame'].includes(incident.itasc.incident.showing ?? '') || threat(incident, 'thermal-runaway');
      if (!fireConfirmed || !threat(incident, 'ev-hv-battery')) return null;
      return {
        category: 'Strategy',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'medium',
        title: 'EV battery fire — sustained pinpointed cooling, not a sweep',
        message:
          'An HV battery fire appears involved. Consider water (not foam/dry powder/CO₂, which give no useful cooling on a battery) applied as a consistent, directed stream onto the identified hotspot rather than a sweeping spray, and be prepared for this to take significantly longer than a conventional vehicle fire. Where exposures are absent and water is limited — a plausible rural scenario — deliberately allowing the battery to burn out under control is a recognised option, not just a fallback.',
        whyShown: ['EV / HV battery flagged', 'Fire confirmed / thermal runaway signs present'],
        suggestedActions: ['Apply directed/pinpointed stream to battery hotspot (not a sweep)', 'Consider TIC-guided cooling with repeat thermal checks'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: 'AFAC-3096', title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)' }],
      };
    },
  },
  {
    id: 'ev-water-resource-demand',
    category: 'Water',
    evaluate: ({ incident, config }) => {
      if (!threat(incident, 'ev-hv-battery')) return null;
      const fireConfirmed =
        ['smoke', 'flame', 'smoke-and-flame'].includes(incident.itasc.incident.showing ?? '') || threat(incident, 'thermal-runaway');
      if (!fireConfirmed) return null;
      const water = waterOf(incident, config);
      const bulkRequested =
        incident.itasc.support.requested.includes('bulk-water') || incident.itasc.support.requested.includes('additional-tanker');
      if (bulkRequested) return null;
      return {
        category: 'Water',
        operationalPriority: 3,
        severity: 'high',
        confidence: 'medium',
        title: 'EV battery fires need far more water than a standard vehicle fire',
        message:
          `Published guidance cites roughly 4,000-30,000 L to fully extinguish and cool an EV battery fire — well beyond a single appliance's onboard water (~${water.onboardWaterL} L currently committed). Consider requesting additional tanker / bulk water early rather than after onboard supply runs low.`,
        whyShown: ['EV / HV battery fire confirmed', 'No bulk water / additional tanker requested yet', `~${water.onboardWaterL} L onboard across responding units`],
        suggestedActions: ['Request additional tanker / bulk water', 'Update water plan'],
        actionSpecs: [null, { type: 'open-panel', panel: 'water' }],
        doctrineRefs: [
          { docId: 'AFAC-3096', title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)' },
          { docId: 'FRNSW-D25-20316', title: 'FRNSW — EVs and EV Charging Equipment in the Built Environment (2025) (stand-in)' },
        ],
      };
    },
  },
  {
    id: 'ev-reignition-monitoring',
    category: 'Investigation',
    minStage: 'Overhaul',
    evaluate: (ctx) => {
      const { incident, config } = ctx;
      const evRisk = threat(incident, 'ev-hv-battery') || threat(incident, 'reignition-risk');
      if (!evRisk) return null;
      const inOverhaulOrLater = ['Overhaul', 'Investigation', 'StandDown'].includes(incident.stage);
      if (!inOverhaulOrLater) return null;
      const resolved = ctx.resolvedParams?.get('evStorageExclusionDistanceM');
      const exclusion = evStorageExclusionRequirement(config, resolved);
      return {
        category: 'Investigation',
        operationalPriority: 4,
        severity: 'medium',
        confidence: 'medium',
        title: 'EV battery — secondary ignition can occur well after knockdown',
        message:
          `A fire-damaged HV battery is involved. Consider continuing thermal monitoring until temperature reads stable at ambient across repeat checks (not just once), keeping the vehicle at least ${exclusion.minMetres} m from exposures/other vehicles while on scene and in any interim storage, and documenting the handover so the receiving party (owner, tow/recovery, insurer) is on notice of an ongoing secondary-ignition risk — public sources document cases igniting again from hours up to several weeks after apparent extinguishment, so no fixed "all clear" time should be assumed.`,
        whyShown: ['EV / HV battery or reignition risk flagged', `Stage: ${incident.stage}`],
        suggestedActions: ['Continue thermal monitoring until stable at ambient (repeat checks)', `Keep ${exclusion.minMetres} m separation from exposures on scene / in storage`, 'Document handover to tow/recovery or owner'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
        doctrineRefs: [{ docId: exclusion.source.docId, title: exclusion.source.title }],
      };
    },
  },

  // ── Hazmat rules ────────────────────────────────────────────────────────────
  {
    id: 'hazmat-scene-safety',
    category: 'Life',
    incidentTypes: ['Hazmat'] as IncidentType[],
    evaluate: ({ incident }) => {
      const approachStages = ['Dispatch', 'EnRoute'] as const;
      if (!approachStages.includes(incident.stage as typeof approachStages[number])) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'high',
        title: 'Hazmat — approach and scene safety',
        message:
          'Approaching a hazmat / chemical incident. Consider a safe, upwind and uphill approach, identifying the substance from a distance where possible (placard, SDS, caller description) before committing crew, and confirming PPE requirements.',
        whyShown: ['Incident type: Hazmat / chemical', `Stage: ${incident.stage}`],
        suggestedActions: ['Approach upwind / uphill', 'Brief crew on hazard before committing'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'hazmat-substance-unidentified',
    category: 'Life',
    incidentTypes: ['Hazmat'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      if (!threat(incident, 'unknown-substance')) return null;
      return {
        category: 'Life',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'high',
        title: 'Hazmat — substance not yet identified',
        message:
          'The substance involved is flagged as unknown. Consider identifying it before further entry (placard, SDS, container labelling, caller callback) and maintaining a safe stand-off distance in the meantime.',
        whyShown: ['Hazmat with substance flagged as unknown'],
        suggestedActions: ['Attempt substance identification', 'Maintain safe stand-off distance', 'Consider FRNSW hazmat / EPA'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'hazmat-vapour-cordon',
    category: 'Exposure',
    incidentTypes: ['Hazmat'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'vapour-cloud')) return null;
      return {
        category: 'Exposure',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'medium',
        title: 'Hazmat — vapour cloud',
        message:
          'A vapour cloud is flagged. Consider establishing an exclusion zone / cordon upwind of the release, evacuating persons in its path, and notifying EPA and affected utilities.',
        whyShown: ['Hazmat with vapour cloud flagged'],
        suggestedActions: ['Establish cordon upwind', 'Consider evacuation in the cloud path', 'Notify EPA'],
        actionSpecs: [null, null, null],
      };
    },
  },

  // ── Storm / flood rules ─────────────────────────────────────────────────────
  {
    id: 'storm-flood-scene-safety',
    category: 'Life',
    incidentTypes: ['StormFlood'] as IncidentType[],
    evaluate: ({ incident }) => {
      const approachStages = ['Dispatch', 'EnRoute'] as const;
      if (!approachStages.includes(incident.stage as typeof approachStages[number])) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'high',
        confidence: 'high',
        title: 'Storm / flood — approach and scene safety',
        message:
          'Approaching a storm / flood incident. Consider downed powerlines, water depth/current not yet known, and vehicle/ground stability before committing crew — brief on these hazards before approach.',
        whyShown: ['Incident type: Storm / flood', `Stage: ${incident.stage}`],
        suggestedActions: ['Brief crew on powerline / water hazards before approach'],
        actionSpecs: [null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'storm-flood-powerline-hazard',
    category: 'Life',
    incidentTypes: ['StormFlood'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'powerlines-down')) return null;
      return {
        category: 'Life',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'high',
        title: 'Storm / flood — powerlines down',
        message:
          'Downed powerlines are flagged. Consider treating them as energised until the network operator confirms isolation, keeping crew and public out of the exclusion zone, and notifying the utility.',
        whyShown: ['Powerlines down flagged'],
        suggestedActions: ['Treat as energised until confirmed isolated', 'Notify utility / network operator', 'Keep exclusion zone clear'],
        actionSpecs: [null, null, null],
        requiresAcknowledgement: true,
      };
    },
  },
  {
    id: 'storm-flood-water-hazard',
    category: 'Life',
    incidentTypes: ['StormFlood'] as IncidentType[],
    evaluate: ({ incident }) => {
      if (!threat(incident, 'rising-water')) return null;
      return {
        category: 'Life',
        operationalPriority: 2,
        severity: 'high',
        confidence: 'medium',
        title: 'Storm / flood — rising water',
        message:
          'Rising water is flagged. Consider confirming depth and flow before any entry, not driving or walking through floodwater, and whether SES flood-rescue capability (and PFDs) is required.',
        whyShown: ['Rising water flagged'],
        suggestedActions: ['Confirm depth/flow before entry', 'Consider requesting SES flood-rescue capability'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
      };
    },
  },

  // ── Alarm rules ─────────────────────────────────────────────────────────────
  {
    id: 'alarm-fire-indicators',
    category: 'Strategy',
    incidentTypes: ['Alarm'] as IncidentType[],
    evaluate: ({ incident }) => {
      const showing = incident.itasc.incident.showing;
      const showingFire = showing === 'smoke' || showing === 'flame' || showing === 'smoke-and-flame';
      if (!showingFire) return null;
      return {
        category: 'Strategy',
        operationalPriority: 1,
        severity: 'critical',
        confidence: 'high',
        title: 'Alarm job showing smoke / flame',
        message:
          'This was paged as an alarm but smoke or flame is now showing. Consider reclassifying as a structure fire and escalating the response (additional appliances, BA, water) rather than continuing to treat it as an automatic alarm.',
        whyShown: [`Showing: ${SHOWING_LABELS[showing!]}`, 'Incident type: Alarm / automatic'],
        suggestedActions: ['Reclassify as structure fire', 'Escalate response (additional appliances, BA, water)'],
        actionSpecs: [null, null],
        requiresAcknowledgement: true,
      };
    },
  },

  // ── Assist other agency rules ──────────────────────────────────────────────
  {
    id: 'assist-agency-role-clarity',
    category: 'Coordination',
    incidentTypes: ['AssistOtherAgency'] as IncidentType[],
    minStage: 'Arrival',
    evaluate: ({ incident }) => {
      if (incident.itasc.command.incidentController?.trim()) return null;
      return {
        category: 'Coordination',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'medium',
        title: 'Assist other agency — confirm role and contact',
        message:
          'Attending in support of another agency. Consider confirming the lead agency’s incident controller / point of contact, exactly what tasking is being requested, and any limits on the resources committed.',
        whyShown: ['Incident type: Assist other agency', 'No IC / point of contact recorded yet'],
        suggestedActions: ['Confirm lead agency IC / contact', 'Confirm tasking and resource limits'],
        actionSpecs: [null, null],
      };
    },
  },

  // ── Cross-type: confirmed non-structure-fire (e.g. an attended, permitted
  // burn-off) ─────────────────────────────────────────────────────────────────
  {
    id: 'confirmed-burn-off-attendance',
    category: 'Workflow',
    evaluate: ({ incident }) => {
      if (incident.status !== 'non-structure-fire') return null;
      return {
        category: 'Workflow',
        operationalPriority: 4,
        severity: 'info',
        confidence: 'medium',
        title: 'Confirmed as burn-off / non-structure fire',
        message:
          'What was paged as a fire has been confirmed as a burn-off / non-structure fire — e.g. an attended, permitted pile burn rather than a job needing suppression. Consider confirming any burn permit / total fire ban status, that it remains attended until safe, and whether neighbours or a complainant need a callback before standing down.',
        whyShown: ['Confirmation: Burn-off / non-structure fire'],
        suggestedActions: ['Confirm permit / fire ban status', 'Confirm attended until safe', 'Consider stand-down'],
        actionSpecs: [null, null, { type: 'set-stage', stage: 'StandDown' }],
      };
    },
  },

  // ── Level escalation (all types) ───────────────────────────────────────────
  {
    id: 'level-escalation-check',
    category: 'Coordination',
    minStage: 'Suppression',
    evaluate: ({ incident }) => {
      if (incident.level !== 'Level1') return null;
      const multiAgency = incident.supportingAgencies.length > 0;
      const noIC = !incident.itasc.command.incidentController?.trim();
      const noChannel = !incident.itasc.command.radioChannel?.trim();
      if (!multiAgency || (!noIC && !noChannel)) return null;
      return {
        category: 'Coordination',
        operationalPriority: 3,
        severity: 'medium',
        confidence: 'medium',
        title: 'Consider Level 2 escalation',
        message:
          'Multi-agency involvement at an extended incident without a confirmed command structure may warrant Level 2 escalation. Consider confirming IC, comms plan, and notifying the duty officer.',
        whyShown: [
          multiAgency ? 'Multiple agencies involved' : '',
          noIC ? 'Incident controller not yet confirmed' : '',
          noChannel ? 'Radio channel not confirmed' : '',
        ].filter(Boolean),
        suggestedActions: [
          'Confirm IC and control structure',
          'Consider Level 2 escalation',
          'Notify duty officer',
        ],
        actionSpecs: [null, null, null],
      };
    },
  },
];
